/* eslint-disable no-async-promise-executor */
import { canvasRequest } from '$/api/canvasRquest';
import { agentTeamRequestApi } from '$/api/agentTeamRequest';
import { submitAgentTeamRoomOnCreate } from '$/domains/teamAgent/teamAgentService';
import APP from '$/api/api';
import { useOfficialStore } from '$/store/useOfficialStore';
import { getTeamCanvasLockTabId } from '$/utils/teamCanvasLockTabId';
import type { WorkflowJSON } from '@flowgram.ai/free-layout-core';
import {
  normalizePublishIpCandidates, normalizePublishIpIds, normalizePublishIpList,
  normalizePublishIpSelection, serializePublishIpRequest,
} from './publishIpIds';
import type {
  CanvasIdentifier,
  CanvasDataTarget,
  CanvasPublishEditV3Params,
  CanvasPublishInfoV3Params,
  CanvasPublishInfoV3Result,
  CanvasPublishIpCandidatesResult,
  CanvasPublishListV3Params,
  CanvasPublishPinV3Params,
  CanvasFavoriteListV3Params,
  CanvasFavoriteListV3Result,
  CanvasMaterialBgmItem,
  CanvasMaterialCameraPresetListV3Result,
  CanvasMaterialGeometryModelItem,
  CanvasMaterialImageItem,
  CanvasMaterialListV3Result,
  CanvasMaterialModelListV3Params,
  CanvasMaterialModelListV3Result,
  CanvasMaterialMovingShotItem,
  CanvasMaterialPresetNodeListV3Params,
  CanvasMaterialPresetNodeListV3Result,
  CanvasMaterialPosePresetListV3Result,
  CanvasMaterialPropModelItem,
  CanvasMaterialPuppetModelItem,
  CanvasMaterialSkeletonParamListV3Result,
  CreateCanvasShareV3Params,
  CreateCanvasShareV3Result,
  GetCanvasShareInfoV3Params,
  GetCanvasShareInfoV3Result,
  GetShareCanvasDataV3Params,
  GetWorkflowDataV3Result,
  ModelConfig,
  PublishMetadata,
  PublishWorkflowV3Params,
  PublishWorkflowV3Result,
  SaveCanvasFavoriteV3Params,
  TimbreItem,
  V3CanvasListItem,
  V3CanvasListResult,
  V3CanvasProjectItem,
  V3CanvasProjectListParams,
  V3CanvasProjectListResult,
  V3CanvasRoomListItem,
  V3CanvasRoomListResult,
  V3CanvasRoomInfo,
  MaterialTimbreListV3Params,
} from './type';

const DEFAULT_PUBLISH_ASSET_URL = 'https://stage.meitudata.com/meitu_account/cc8c87cf1d627d2db5d72b234ef832cb.jpg';
const DEFAULT_PUBLISH_TITLE = 'RoboNeo Team Studio';
const DEFAULT_PUBLISH_DESCRIPTION = 'RoboNeo Team Studio publish flow';
const DEFAULT_AUDIT_POLL_INTERVAL_MS = 2000;
const DEFAULT_AUDIT_POLL_MAX_ATTEMPTS = 30;

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function getResponseMessage(response: any, fallback: string) {
  return response?.data?.message || response?.message || fallback;
}

function assertSuccessResponse(response: any, fallback: string) {
  if (response?.status === 200 && response?.data?.code === 0) {
    return response.data.data;
  }
  throw new Error(getResponseMessage(response, fallback));
}

/** 统一处理 v3 画布素材 GET 列表接口的成功结构与失败回传。 */
async function getCanvasMaterialListV3<T = unknown>(url: string, params: Record<string, any> = {}) {
  const response = await canvasRequest.request({
    url,
    method: 'GET',
    params,
  });
  if (response.status === 200 && response.data.code === 0) {
    return response.data.data as T;
  }
  throw response;
}

function getRecordValue(data: unknown, keys: string[]) {
  if (!data || typeof data !== 'object') return undefined;
  const record = data as Record<string, any>;
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null && record[key] !== '') {
      return record[key];
    }
  }
  return undefined;
}

function normalizeTaskId(data: unknown) {
  if (typeof data === 'string' || typeof data === 'number') {
    return String(data);
  }

  const directValue = getRecordValue(data, ['task_id', 'taskId', 'id']);
  if (directValue !== undefined) return String(directValue);

  const nestedTask = getRecordValue(data, ['task']);
  const nestedValue = getRecordValue(nestedTask, ['task_id', 'taskId', 'id']);
  return nestedValue !== undefined ? String(nestedValue) : '';
}

function normalizeAuditRecordId(data: unknown) {
  if (typeof data === 'number') return data;
  if (typeof data === 'string' && data.trim()) return Number(data);

  const directValue = getRecordValue(data, ['audit_record_id', 'auditRecordId', 'record_id', 'recordId', 'id']);
  if (directValue !== undefined) return Number(directValue);

  const nestedRecord = getRecordValue(data, ['audit_record', 'auditRecord', 'record']);
  const nestedValue = getRecordValue(nestedRecord, ['audit_record_id', 'auditRecordId', 'record_id', 'recordId', 'id']);
  return nestedValue !== undefined ? Number(nestedValue) : NaN;
}

function getAuditStatus(data: unknown) {
  const status = getRecordValue(data, ['audit_status', 'auditStatus', 'status']);
  return typeof status === 'string' ? status.toLowerCase() : '';
}

function isAuditRejected(data: unknown) {
  const status = getAuditStatus(data);
  return ['fail', 'failed', 'failure', 'reject', 'rejected', 'error'].includes(status);
}

function getDefaultPublishMetadata(metadata?: Partial<PublishMetadata>): PublishMetadata {
  return {
    work_asset_url: metadata?.work_asset_url || DEFAULT_PUBLISH_ASSET_URL,
    cover_asset_url: metadata?.cover_asset_url || DEFAULT_PUBLISH_ASSET_URL,
    title: metadata?.title || DEFAULT_PUBLISH_TITLE,
    description: metadata?.description || DEFAULT_PUBLISH_DESCRIPTION,
  };
}

function getDefaultPublishRegion(region?: string) {
  return region || useOfficialStore.getState().currentCountryCode || 'CN';
}

/**
 * 获取 Agent 团队工作流 v3 画布数据
 *
 * @export
 * @param {string} [room_id]
 * @param {string} [share_code]
 * @param {string} [case_id]
 * @return {*}
 */
export async function forkCanvasDataV3(params: {
  room_id: string;
  share_code?: string;
  case_id?: number;
}) {
  return new Promise<{
    data: string;
  }>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasDataForkV3,
      method: 'POST',
      data: {
        ...params,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data);
      return;
    }
    reject(response);
  });
}

/**
 * 判断 v3 画布 data/get 是否表示项目已删除或不存在。
 */
export function isCanvasProjectUnavailable(payload?: {
  project_exist?: boolean;
  project_deleted?: boolean;
} | null): boolean {
  if (!payload) {
    return false;
  }
  if (payload.project_deleted === true) {
    return true;
  }
  if (payload.project_exist === false) {
    return true;
  }
  return false;
}

/** 画布数据只使用项目与画布 ID，不再兼容以会话 ID 读取。 */
export async function getWorkflowDataV3(target: CanvasDataTarget, share_code?: string): Promise<GetWorkflowDataV3Result> {
  if (!target.project_id || !target.canvas_id) throw new Error('Missing project_id or canvas_id');
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasDataV3,
    method: 'GET',
    params: {
      project_id: target.project_id,
      canvas_id: target.canvas_id,
      share_code,
    },
  });
  if (response.status === 200 && response.data.code === 0) return response.data.data;
  return Promise.reject(response);
}

/**
 * 获取 v3 画布分享信息（房间列表、定位房画布 data、Agent short_link）
 *
 * @export
 * @param {GetCanvasShareInfoV3Params} params
 * @return {*}
 */
export async function getCanvasShareInfoV3(
  params: GetCanvasShareInfoV3Params,
): Promise<GetCanvasShareInfoV3Result> {
  const requestParams: Record<string, string> = {};
  if (params.share_code) {
    requestParams.share_code = params.share_code;
  }
  if (params.project_id) {
    requestParams.project_id = params.project_id;
  }
  if (params.room_id) {
    requestParams.room_id = params.room_id;
  }
  const response = await canvasRequest.request({
    url: APP.roboneo.api.shareCanvasDataV3,
    method: 'GET',
    params: requestParams,
    skipErrorRedirect: params.skipErrorRedirect,
  });
  if (response.status === 200 && response.data.code === 0) {
    return response.data.data;
  }
  throw response;
}

/**
 * 获取 Agent 团队工作流 v3 分享画布数据（兼容旧名，等同 getCanvasShareInfoV3）
 *
 * @export
 * @param {GetShareCanvasDataV3Params} params
 * @return {*}
 */
export async function getShareCanvasDataV3(params: GetShareCanvasDataV3Params) {
  return getCanvasShareInfoV3(params);
}

/**
 * 从 share/info 的 rooms 列表中按 sort 取第一个画布 room_id（同 sort 时优先非 project 房）。
 *
 * @param {GetCanvasShareInfoV3Result} info
 * @return {*}
 */
export function pickFirstShareCanvasRoomId(info: GetCanvasShareInfoV3Result): string | undefined {
  if (!info.rooms?.length) {
    return info.room_id ? String(info.room_id) : undefined;
  }

  const projectId = info.project_id ? String(info.project_id) : '';
  const sortedRooms = [...info.rooms].sort((a, b) => {
    const sortA = typeof a.sort === 'number' ? a.sort : Number.MAX_SAFE_INTEGER;
    const sortB = typeof b.sort === 'number' ? b.sort : Number.MAX_SAFE_INTEGER;
    if (sortA !== sortB) {
      return sortA - sortB;
    }
    const aIsProject = projectId && String(a.room_id) === projectId;
    const bIsProject = projectId && String(b.room_id) === projectId;
    if (aIsProject !== bIsProject) {
      return aIsProject ? 1 : -1;
    }
    return 0;
  });

  const firstRoomId = sortedRooms[0]?.room_id;
  return firstRoomId ? String(firstRoomId) : undefined;
}

/**
 * 按 URL 中的 share_code / short_link 在 rooms 里匹配目标画布 room_id。
 * 多个 short_link 相同时优先非 project 房，再按 sort 降序取最新画布。
 *
 * @param {GetCanvasShareInfoV3Result} info
 * @param {string} [shareToken] URL 上的 share_code 或 short_link
 * @return {*}
 */
export function pickShareCanvasRoomIdByToken(
  info: GetCanvasShareInfoV3Result,
  shareToken?: string,
): string | undefined {
  const token = shareToken?.trim();
  if (!token) {
    return pickFirstShareCanvasRoomId(info);
  }

  const projectId = info.project_id ? String(info.project_id) : '';
  const matchedRooms = (info.rooms ?? []).filter(
    (room) => room.short_link && String(room.short_link) === token,
  );

  if (matchedRooms.length > 0) {
    const canvasMatches = matchedRooms.filter(
      (room) => !projectId || String(room.room_id) !== projectId,
    );
    const candidates = canvasMatches.length > 0 ? canvasMatches : matchedRooms;
    const sorted = [...candidates].sort((a, b) => {
      const sortA = typeof a.sort === 'number' ? a.sort : Number.MIN_SAFE_INTEGER;
      const sortB = typeof b.sort === 'number' ? b.sort : Number.MIN_SAFE_INTEGER;
      return sortB - sortA;
    });
    const matchedRoomId = sorted[0]?.room_id;
    if (matchedRoomId) {
      return String(matchedRoomId);
    }
  }

  const topShortLink = info.short_link ? String(info.short_link) : '';
  const topShareCode = info.share_code ? String(info.share_code) : '';
  if (token === topShortLink || token === topShareCode) {
    const topRoomId = info.room_id ? String(info.room_id) : '';
    if (topRoomId && projectId && topRoomId !== projectId) {
      return topRoomId;
    }
    return pickFirstShareCanvasRoomId(info);
  }

  return pickFirstShareCanvasRoomId(info);
}

/**
 * 加载分享页信息：按 share_code/short_link 匹配 rooms，再拉取对应房 data。
 *
 * @export
 * @param {GetCanvasShareInfoV3Params} params
 * @return {*}
 */
export async function loadCanvasShareInfoV3(params: GetCanvasShareInfoV3Params) {
  const shareToken = params.share_code?.trim();
  const preferredRoomId = params.room_id?.trim();
  const projectId = params.project_id?.trim();
  const initial = await getCanvasShareInfoV3({
    share_code: shareToken || params.share_code,
    project_id: projectId || undefined,
    room_id: preferredRoomId || undefined,
    skipErrorRedirect: params.skipErrorRedirect,
  });

  const entryShareCode = initial.share_code?.trim() || shareToken;
  const resolvedProjectId = projectId || (initial.project_id ? String(initial.project_id) : undefined);
  const targetRoomId = preferredRoomId || pickShareCanvasRoomIdByToken(initial, entryShareCode);
  if (!targetRoomId) {
    return {
      ...initial,
      share_code: entryShareCode || initial.share_code,
    };
  }

  const initialRoomId = initial.room_id ? String(initial.room_id) : '';
  if (initialRoomId === targetRoomId && initial.data) {
    return {
      ...initial,
      room_id: targetRoomId,
      share_code: entryShareCode || initial.share_code,
    };
  }

  const detail = await getCanvasShareInfoV3({
    share_code: entryShareCode || params.share_code || initial.share_code,
    project_id: resolvedProjectId,
    room_id: targetRoomId,
    skipErrorRedirect: params.skipErrorRedirect,
  }).catch(() => null);

  if (!detail) {
    return {
      ...initial,
      room_id: targetRoomId,
      share_code: entryShareCode || initial.share_code,
    };
  }

  return {
    ...detail,
    room_id: targetRoomId,
    rooms: detail.rooms ?? initial.rooms,
    share_code: detail.share_code ?? initial.share_code ?? entryShareCode,
    project_id: detail.project_id ?? initial.project_id,
    // detail 是按具体画布拉的，name 会退化为画布名；分享页顶栏需要项目名，用 initial.name 兜住。
    name: initial.name ?? detail.name,
  };
}

/**
 * 创建或更新 v3 画布分享入口（项目级分享）
 *
 * @export
 * @param {CreateCanvasShareV3Params} params
 * @return {*}
 */
export async function createCanvasShareV3(params: CreateCanvasShareV3Params) {
  return new Promise<CreateCanvasShareV3Result>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasShareV3,
      method: 'POST',
      data: params,
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data);
      return;
    }
    reject(response);
  });
}

/**
 * 保存 Agent 团队工作流 v3 画布数据
 *
 * @export
 * @param {CanvasDataTarget} target 项目和画布身份
 * @param {WorkflowJSON} data
 * @param {string} [tabId] 浏览器页签 ID，默认与画布锁 tab_id 一致
 * @return {*}
 */
export async function saveWorkflowDataV3(target: CanvasDataTarget, data: WorkflowJSON, tabId?: string) {
  if (!target.project_id || !target.canvas_id) throw new Error('Missing project_id or canvas_id');
  return new Promise<{
    data: string;
  }>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.saveCanvasDataV3,
      method: 'POST',
      data: {
        project_id: target.project_id,
        canvas_id: target.canvas_id,
        tab_id: tabId || getTeamCanvasLockTabId(),
        data: JSON.stringify(data),
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data);
      return;
    }
    reject(response);
  });
}

/**
 * 获取 Agent 团队工作流 v3 房间信息
 *
 * @export
 * @param {string} roomId
 * @return {*}
 */
export async function getCanvasRoomInfoV3(roomId: string) {
  return new Promise<V3CanvasRoomInfo>(async (resolve, reject) => {
    try {
      const response = await canvasRequest.request({
        url: APP.roboneo.api.canvasRoomInfoV3,
        method: 'GET',
        params: {
          room_id: roomId,
        },
      });
      if (response.status === 200 && response.data.code === 0) {
        resolve(response.data.data);
        return;
      }
      reject(response);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * 创建 Agent 团队工作流 v3 项目
 *
 * @export
 * @param {{ name?: string; roomId?: string; roomType?: string; }} params
 * @return {*}
 */
export async function createCanvasProjectV3(params: {
  name?: string;
  roomId?: string;
  roomType?: 'workflow' | 'agent';
} = {}) {
  return new Promise<V3CanvasRoomInfo>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasProjectCreateV3,
      method: 'POST',
      data: {
        name: params.name,
        room_id: params.roomId,
        room_type: params.roomType || 'workflow',
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data);
      return;
    }
    reject(response);
  });
}

/**
 * 删除 Agent 团队工作流 v3 项目
 *
 * @export
 * @param {string | number} projectId
 * @return {*}
 */
export async function deleteCanvasProjectV3(projectId: string | number) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasProjectDeleteV3,
      method: 'POST',
      // 个人项目操作固定读取个人空间，团队态下也不能携带 Client-OID。
      skipClientOId: true,
      data: {
        project_id: projectId,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data);
      return;
    }
    reject(response);
  });
}

/**
 * 重命名 Agent 团队工作流 v3 房间
 *
 * @export
 * @param {string} projectId
 * @param {string} roomId
 * @param {string} name
 * @return {*}
 */
export async function renameCanvasRoomV3(projectId: string, roomId: string, name: string) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasRoomRenameV3,
      method: 'POST',
      data: {
        project_id: projectId,
        room_id: roomId,
        name,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data);
      return;
    }
    reject(response);
  });
}

/**
 * 获取 v3 项目列表
 *
 * @export
 * @param {V3CanvasProjectListParams} [params]
 * @return {Promise<V3CanvasProjectListResult>}
 */
export async function getCanvasProjectListV3(params: V3CanvasProjectListParams = {}) {
  return new Promise<V3CanvasProjectListResult>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasProjectListV3,
      method: 'GET',
      params,
      // 个人项目接口固定读取个人空间，团队态下也不能携带 Client-OID。
      skipClientOId: true,
    });
    if (response.status === 200 && response.data.code === 0) {
      const data = response.data.data || {};
      resolve({
        list: (data.list || []) as V3CanvasProjectItem[],
        next_cursor: data.next_cursor == null ? undefined : String(data.next_cursor),
        has_more: data.has_more === true || Number(data.has_more) === 1,
      });
      return;
    }
    reject(response);
  });
}

/**
 * 更新 / 重命名 v3 项目（POST /v3/canvas/project/update.json）
 *
 * @export
 * @param {string} projectId
 * @param {{ name?: string; cover_url?: string; }} payload
 * @return {*}
 */
export async function updateCanvasProjectV3(
  projectId: string,
  payload: { name?: string; cover_url?: string; },
) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasProjectUpdateV3,
      method: 'POST',
      // 个人项目操作固定读取个人空间，团队态下也不能携带 Client-OID。
      skipClientOId: true,
      data: {
        project_id: projectId,
        ...payload,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data || {});
      return;
    }
    reject(response);
  });
}

/**
 * 按项目获取 v3 画布列表（GET /v3/canvas/list.json?project_id=）
 *
 * @export
 * @param {string} projectId
 * @return {Promise<V3CanvasListResult>}
 */
export async function getCanvasListV3(projectId: string) {
  return new Promise<V3CanvasListResult>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasListV3,
      method: 'GET',
      params: {
        project_id: projectId,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve({
        list: (response.data.data?.list || []) as V3CanvasListItem[],
      });
      return;
    }
    reject(response);
  });
}

/**
 * 获取个人项目内的对话房间列表（GET /v3/canvas/room/list.json）。
 * 该列表与画布列表独立，固定跳过团队身份头以读取个人空间。
 */
export async function getCanvasRoomListV3(projectId: string): Promise<V3CanvasRoomListResult> {
  return new Promise<V3CanvasRoomListResult>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasRoomListV3,
      method: 'GET',
      params: {
        project_id: projectId,
      },
      skipClientOId: true,
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve({
        list: (response.data.data?.list || []) as V3CanvasRoomListItem[],
      });
      return;
    }
    reject(response);
  });
}

/** 删除个人项目内的 Agent 房间（POST /v3/canvas/room/delete.json）。 */
export async function deleteCanvasRoomV3(projectId: string, roomId: string) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasRoomDeleteV3,
      method: 'POST',
      skipClientOId: true,
      data: { project_id: projectId, room_id: roomId },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data || {});
      return;
    }
    reject(response);
  });
}

/**
 * 创建 / 绑定 v3 画布到项目（POST /v3/canvas/create.json）
 * project_id 空则新建项目；room_id 空则默认等于 project_id。
 * 网络拒绝直接交给调用方处理，避免创建流程一直等待而无法重试。
 *
 * @export
 * @param {{ project_id?: string; room_id?: string; name?: string; case_id?: string | number; }} params
 * @return {Promise<{ project_id: string; room_id: string; canvas_id: string; name?: string; }>}
 */
export async function createCanvasV3(params: {
  project_id?: string;
  room_id?: string;
  name?: string;
  case_id?: string | number;
} = {}): Promise<{
    project_id: string;
    room_id: string;
    canvas_id: string;
    name?: string;
  }> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasCreateV3,
    method: 'POST',
    // 个人画布/项目创建固定读取个人空间，团队态下也不能携带 Client-OID。
    skipClientOId: true,
    data: {
      project_id: params.project_id,
      room_id: params.room_id,
      name: params.name,
      case_id: params.case_id == null ? undefined : Number(params.case_id),
      alloc_canvas_id: true,
    },
  });
  if (response.status === 200 && response.data.code === 0) {
    const { data } = response.data;
    // room/submit must use the Agent room created before canvas binding, not the canvas response identity.
    const submitRoomId = params.room_id == null ? '' : String(params.room_id).trim();
    if (submitRoomId) {
      await submitAgentTeamRoomOnCreate(submitRoomId);
    }
    return data;
  }
  return Promise.reject(response);
}

/**
 * 删除个人版项目内单张画布（POST /v3/canvas/delete.json）。
 *
 * @export
 * @param {string} projectId
 * @param {CanvasIdentifier} roomId 传 canvas_id 操作独立画布；字符串参数兼容旧 room_id。
 * @return {Promise<Record<string, never>>}
 */
export async function deleteCanvasV3(projectId: string, roomId: CanvasIdentifier) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasDeleteV3,
      method: 'POST',
      // 个人画布操作固定读取个人空间，不能携带当前团队身份头。
      skipClientOId: true,
      data: {
        project_id: projectId,
        ...(typeof roomId === 'string' ? { room_id: roomId } : roomId),
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data || {});
      return;
    }
    reject(response);
  });
}

/**
 * 创建个人版画布副本（POST /v3/canvas/duplicate.json），新 room_id 由服务端生成。
 *
 * @export
 * @param {string} projectId
 * @param {CanvasIdentifier} roomId 传 canvas_id 操作独立画布；字符串参数兼容旧 room_id。
 * @return {Promise<{ project_id: string; room_id: string; name?: string; }>}
 */
export async function duplicateCanvasV3(projectId: string, roomId: CanvasIdentifier) {
  return new Promise<{
    project_id: string;
    room_id: string;
    canvas_id?: string;
    name?: string;
  }>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasDuplicateV3,
      method: 'POST',
      // 个人画布操作固定读取个人空间，不能携带当前团队身份头。
      skipClientOId: true,
      data: {
        project_id: projectId,
        ...(typeof roomId === 'string' ? { room_id: roomId } : roomId),
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      const { data } = response.data;
      if (typeof roomId === 'string' && data?.room_id) {
        await submitAgentTeamRoomOnCreate(String(data.room_id));
      }
      resolve(data);
      return;
    }
    reject(response);
  });
}

/**
 * 重命名 v3 画布（POST /v3/canvas/rename.json，需 project_id + name）
 *
 * @export
 * @param {string} projectId
 * @param {CanvasIdentifier} roomId 传 canvas_id 操作独立画布；字符串参数兼容旧 room_id。
 * @param {string} name
 * @return {*}
 */
export async function renameCanvasV3(projectId: string, roomId: CanvasIdentifier, name: string) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasRenameV3,
      method: 'POST',
      data: {
        project_id: projectId,
        ...(typeof roomId === 'string' ? { room_id: roomId } : roomId),
        name,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data || {});
      return;
    }
    reject(response);
  });
}

/**
 * 获取工作流3.0画布配置
 *
 * @export
 * @return {*}
 */
export async function getCanvasConfigV3() {
  return new Promise<{
    models: ModelConfig;
  }>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasConfigV3,
      method: 'GET',
      params: {},
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data);
      return;
    }
    reject(response);
  });
}

/**
 * 获取 v3 画布 BGM 素材列表
 *
 * @export
 * @return {*}
 */
export async function getCanvasMaterialBgmListV3() {
  return getCanvasMaterialListV3<CanvasMaterialListV3Result<CanvasMaterialBgmItem>>(APP.roboneo.api.canvasMaterialBgmListV3);
}

/**
 * 获取 v3 画布风格素材列表
 *
 * @export
 * @return {*}
 */
export async function getCanvasMaterialStyleListV3() {
  return getCanvasMaterialListV3<CanvasMaterialListV3Result<CanvasMaterialImageItem>>(APP.roboneo.api.canvasMaterialStyleListV3);
}

/**
 * 获取 v3 画布场景素材列表
 *
 * @export
 * @return {*}
 */
export async function getCanvasMaterialSceneListV3() {
  return getCanvasMaterialListV3<CanvasMaterialListV3Result<CanvasMaterialImageItem>>(APP.roboneo.api.canvasMaterialSceneListV3);
}

/**
 * 获取 v3 画布角色素材列表
 *
 * @export
 * @return {*}
 */
export async function getCanvasMaterialCharacterListV3() {
  return getCanvasMaterialListV3<CanvasMaterialListV3Result<CanvasMaterialImageItem>>(APP.roboneo.api.canvasMaterialCharacterListV3);
}

/**
 * 获取 v3 画布道具模型素材列表，并透传游标分页参数。
 *
 * @export
 * @param {CanvasMaterialModelListV3Params} params
 * @return {*}
 */
export async function getCanvasMaterialPropModelListV3(
  params: CanvasMaterialModelListV3Params = {},
) {
  return getCanvasMaterialListV3<CanvasMaterialModelListV3Result<CanvasMaterialPropModelItem>>(
    APP.roboneo.api.canvasMaterialPropModelListV3,
    params,
  );
}

/**
 * 获取 v3 画布人偶模型素材列表，并透传游标分页参数。
 *
 * @export
 * @param {CanvasMaterialModelListV3Params} params
 * @return {*}
 */
export async function getCanvasMaterialPuppetModelListV3(
  params: CanvasMaterialModelListV3Params = {},
) {
  return getCanvasMaterialListV3<CanvasMaterialModelListV3Result<CanvasMaterialPuppetModelItem>>(
    APP.roboneo.api.canvasMaterialPuppetModelListV3,
    params,
  );
}

/**
 * 获取 v3 画布几何模型素材列表，并透传游标分页参数。
 *
 * @export
 * @param {CanvasMaterialModelListV3Params} params
 * @return {*}
 */
export async function getCanvasMaterialGeometryModelListV3(
  params: CanvasMaterialModelListV3Params = {},
) {
  return getCanvasMaterialListV3<CanvasMaterialModelListV3Result<CanvasMaterialGeometryModelItem>>(
    APP.roboneo.api.canvasMaterialGeometryModelListV3,
    params,
  );
}

/** 获取 v3 画布相机机位预设素材列表，并透传游标分页参数。 */
export async function getCanvasMaterialCameraPresetListV3(
  params: CanvasMaterialModelListV3Params = {},
) {
  return getCanvasMaterialListV3<CanvasMaterialCameraPresetListV3Result>(
    APP.roboneo.api.canvasMaterialCameraPresetListV3,
    params,
  );
}

/** 获取 v3 画布骨骼参数素材列表，并透传游标分页参数。 */
export async function getCanvasMaterialSkeletonParamListV3(
  params: CanvasMaterialModelListV3Params = {},
) {
  return getCanvasMaterialListV3<CanvasMaterialSkeletonParamListV3Result>(
    APP.roboneo.api.canvasMaterialSkeletonParamListV3,
    params,
  );
}

/** 获取 v3 画布人偶姿势预设素材列表，并透传游标分页参数。 */
export async function getCanvasMaterialPosePresetListV3(
  params: CanvasMaterialModelListV3Params = {},
) {
  return getCanvasMaterialListV3<CanvasMaterialPosePresetListV3Result>(
    APP.roboneo.api.canvasMaterialPosePresetListV3,
    params,
  );
}

/**
 * 获取 v3 画布运镜素材列表
 *
 * @export
 * @return {*}
 */
export async function getCanvasMaterialMovingShotListV3() {
  return getCanvasMaterialListV3<CanvasMaterialListV3Result<CanvasMaterialMovingShotItem>>(APP.roboneo.api.canvasMaterialMovingShotListV3);
}

/**
 * 获取 v3 画布预设节点列表，默认由调用方传入触发区域等筛选条件。
 *
 * @export
 * @param {{ ids?: string; trigger_area?: string; node_type?: string; app_id?: string; client_id?: string; }} params
 * @return {*}
 */
export async function getCanvasMaterialPresetNodeListV3(params: CanvasMaterialPresetNodeListV3Params = {}) {
  return getCanvasMaterialListV3<CanvasMaterialPresetNodeListV3Result>(
    APP.roboneo.api.canvasMaterialPresetNodeListV3,
    params,
  );
}

/**
 * 获取 v3 画布收藏节点列表。
 *
 * @export
 * @param {{ page?: string; count?: string; }} params
 * @return {*}
 */
export async function getCanvasFavoriteListV3(params: CanvasFavoriteListV3Params = {}) {
  return getCanvasMaterialListV3<CanvasFavoriteListV3Result>(
    APP.roboneo.api.canvasFavoriteListV3,
    params,
  );
}

/**
 * 获取音色素材；可选 signal 撤销关闭菜单或身份切换后的列表请求。
 *
 * @export
 * @return {*}
 */
export async function getMaterialTimbreListV3(params: MaterialTimbreListV3Params, signal?: AbortSignal) {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasMaterialTtsTimbreListV3,
    method: 'GET',
    params,
    signal,
  });
  if (response.status === 200 && response.data.code === 0) {
    return response.data.data;
  }
  throw response;
}

/**
 * 获取音色收藏列表
 *
 * @export
 * @return {*}
 */
export async function getMaterialTimbreFavoriteList(count = 20) {
  return new Promise<TimbreItem[]>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.ttsTimbreFavoriteList,
      method: 'GET',
      params: {
        count,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data.list);
      return;
    }
    reject(response);
  });
}

/**
 * 提交发布审核
 *
 * @export
 * @param {string} roomId
 * @param {Partial<PublishMetadata>} [metadata]
 * @return {*}
 */
export async function submitPublishAuditV3(roomId: string, metadata?: Partial<PublishMetadata>) {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasPublishAuditSubmitV3,
    method: 'POST',
    data: {
      room_id: roomId,
      ...getDefaultPublishMetadata(metadata),
    },
  });
  const data = assertSuccessResponse(response, 'publish audit submit failed');
  const taskId = normalizeTaskId(data);
  if (!taskId) {
    throw new Error('publish audit submit response missing task_id');
  }
  return {
    taskId,
    data,
  };
}

export async function getPublishAuditStatusV3(taskId: string) {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasPublishAuditStatusV3,
    method: 'GET',
    params: {
      task_id: taskId,
    },
  });
  return assertSuccessResponse(response, 'publish audit status failed');
}

export async function pollPublishAuditStatusV3({
  taskId,
  intervalMs = DEFAULT_AUDIT_POLL_INTERVAL_MS,
  maxAttempts = DEFAULT_AUDIT_POLL_MAX_ATTEMPTS,
}: {
  taskId: string;
  intervalMs?: number;
  maxAttempts?: number;
}) {
  let lastData: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const data = await getPublishAuditStatusV3(taskId);
    lastData = data;

    if (isAuditRejected(data)) {
      throw new Error('publish audit rejected');
    }

    const auditRecordId = normalizeAuditRecordId(data);
    if (Number.isFinite(auditRecordId) && auditRecordId > 0) {
      return {
        auditRecordId,
        data,
      };
    }

    if (attempt < maxAttempts - 1) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(intervalMs);
    }
  }

  throw new Error(`publish audit timeout${lastData ? `: ${JSON.stringify(lastData)}` : ''}`);
}

/** 保留显式关闭和清空，不替旧调用方补默认值，避免编辑时覆盖作者选择。 */
function getPublishOptions(metadata?: Partial<PublishMetadata>) {
  return {
    ...(typeof metadata?.workflow_public === 'boolean' ? { workflow_public: metadata.workflow_public } : {}),
    ...(metadata?.ip_ids !== undefined ? { ip_ids: normalizePublishIpIds(metadata.ip_ids) } : {}),
  };
}

/** 审核通过后提交发布内容及作者明确选择的公开范围。 */
export async function submitPublishV3({
  roomId,
  auditRecordId,
  metadata,
  region,
}: {
  roomId: string;
  auditRecordId: number;
  metadata?: Partial<PublishMetadata>;
  region?: string;
}) {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasPublishSubmitV3,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [serializePublishIpRequest],
    data: {
      room_id: roomId,
      audit_record_id: auditRecordId,
      ...getDefaultPublishMetadata(metadata),
      ...getPublishOptions(metadata),
      region: getDefaultPublishRegion(region),
    },
  });
  return assertSuccessResponse(response, 'publish submit failed');
}

async function runPublishAuditV3({
  roomId,
  metadata,
  pollIntervalMs = DEFAULT_AUDIT_POLL_INTERVAL_MS,
  pollMaxAttempts = DEFAULT_AUDIT_POLL_MAX_ATTEMPTS,
}: {
  roomId: string;
  metadata?: Partial<PublishMetadata>;
  pollIntervalMs?: number;
  pollMaxAttempts?: number;
}) {
  const publishMetadata = getDefaultPublishMetadata(metadata);
  const { taskId } = await submitPublishAuditV3(roomId, publishMetadata);
  const { auditRecordId } = await pollPublishAuditStatusV3({
    taskId,
    intervalMs: pollIntervalMs,
    maxAttempts: pollMaxAttempts,
  });

  return {
    taskId,
    auditRecordId,
    publishMetadata,
  };
}

/**
 * 获取 Agent 团队工作流 v3 发布内容列表
 *
 * @export
 * @param {CanvasPublishListV3Params} params
 * @return {*}  {Promise<unknown>}
 */
export async function getCanvasPublishListV3(params: CanvasPublishListV3Params): Promise<unknown> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasPublishListV3,
    method: 'GET',
    params: {
      page: params.page,
      count: params.count,
    },
  });
  return normalizePublishIpList(assertSuccessResponse(response, 'canvas publish list failed'));
}

/**
 * 获取 Agent 团队工作流 v3 房间最新发布提交信息
 *
 * @export
 * @param {CanvasPublishInfoV3Params} params
 * @return {*}  {Promise<CanvasPublishInfoV3Result>}
 */
export async function getCanvasPublishInfoV3(params: CanvasPublishInfoV3Params): Promise<CanvasPublishInfoV3Result> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasPublishInfoV3,
    method: 'GET',
    params: {
      room_id: params.room_id,
    },
  });
  return normalizePublishIpSelection(assertSuccessResponse(response, 'canvas publish info failed')) as CanvasPublishInfoV3Result;
}

/** 读取当前画布的去重出演候选；GET 沿用公共登录及空间上下文。 */
export async function getCanvasPublishIpCandidatesV3(params: CanvasPublishInfoV3Params): Promise<CanvasPublishIpCandidatesResult> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasPublishIpCandidatesV3,
    method: 'GET',
    params: { room_id: params.room_id },
  });
  return normalizePublishIpCandidates(assertSuccessResponse(response, 'canvas publish IP candidates failed'));
}

/**
 * 删除 Agent 团队工作流 v3 发布内容
 *
 * @export
 * @param {string | number} publicationId
 * @return {*}  {Promise<unknown>}
 */
export async function canvasPublishDeleteV3(params: { publication_id?: number; room_id?: string; }): Promise<unknown> {
  const data = {
    ...(params.publication_id !== undefined ? { publication_id: Number(params.publication_id) } : {}),
    ...(params.room_id ? { room_id: params.room_id } : {}),
  };
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasPublishDeleteV3,
    method: 'POST',
    data,
  });
  return assertSuccessResponse(response, 'canvas publish delete failed');
}

/**
 * 编辑 Agent 团队工作流 v3 发布更新
 *
 * @export
 * @param {CanvasPublishEditV3Params} params
 * @return {*}  {Promise<unknown>}
 */
export async function canvasPublishUpdateV3(params: CanvasPublishEditV3Params): Promise<unknown> {
  const publishOptions = getPublishOptions(params);
  const { auditRecordId, publishMetadata } = await runPublishAuditV3({
    roomId: params.room_id,
    metadata: params,
    pollIntervalMs: params.pollIntervalMs,
    pollMaxAttempts: params.pollMaxAttempts,
  });
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasPublishEditV3,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [serializePublishIpRequest],
    data: {
      publication_id: Number(params.publication_id),
      audit_record_id: auditRecordId,
      work_asset_url: publishMetadata.work_asset_url,
      cover_asset_url: publishMetadata.cover_asset_url,
      title: publishMetadata.title,
      description: publishMetadata.description,
      ...publishOptions,
    },
  });
  return assertSuccessResponse(response, 'canvas publish edit failed');
}

/**
 * 设置 Agent 团队工作流 v3 发布内容置顶状态
 *
 * @export
 * @param {CanvasPublishPinV3Params} params
 * @return {*}  {Promise<unknown>}
 */
export async function canvasPublishCasePinV3(params: CanvasPublishPinV3Params): Promise<unknown> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.canvasPublishCasePinV3,
    method: 'POST',
    data: {
      publication_id: Number(params.publication_id),
      pin: params.pin,
    },
  });
  return assertSuccessResponse(response, 'canvas publish pin failed');
}

/** 沿用机审、状态轮询和发布顺序，公开配置仅随最终发布请求提交。 */
export async function publishWorkflowV3({
  roomId,
  metadata,
  region,
  pollIntervalMs = DEFAULT_AUDIT_POLL_INTERVAL_MS,
  pollMaxAttempts = DEFAULT_AUDIT_POLL_MAX_ATTEMPTS,
}: PublishWorkflowV3Params): Promise<PublishWorkflowV3Result> {
  const publishOptions = getPublishOptions(metadata);
  const { taskId, auditRecordId, publishMetadata } = await runPublishAuditV3({
    roomId,
    metadata,
    pollIntervalMs,
    pollMaxAttempts,
  });
  const publishResult = await submitPublishV3({
    roomId,
    auditRecordId,
    metadata: { ...publishMetadata, ...publishOptions },
    region,
  });

  return {
    taskId,
    auditRecordId,
    publishResult,
  };
}

/**
 * 保存画布收藏（我的节点）
 *
 * @export
 * @param {SaveCanvasFavoriteV3Params} params
 * @return {*}
 */
export async function saveCanvasFavoriteV3(params: SaveCanvasFavoriteV3Params) {
  return new Promise<unknown>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasFavoriteSaveV3,
      method: 'POST',
      data: {
        name: params.name,
        cover_url: params.cover_url,
        graph_snapshot: params.graph_snapshot,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data);
      return;
    }
    reject(response);
  });
}

/**
 * 登记画布 V3 分享短链（需登录；client_id 由 canvas 请求公共参数注入）
 *
 * @export
 * @param {string} roomId
 * @param {string} shortLink
 * @return {*}
 */
export async function registerCanvasV3Share(params: { roomId: string; shortLink: string; }) {
  return new Promise<void>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.canvasShareV3,
      method: 'POST',
      data: {
        room_id: params.roomId,
        short_link: params.shortLink,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve();
      return;
    }
    reject(response);
  });
}

/**
 * 设置音色收藏状态
 *
 * @export
 * @param {string} material_id
 * @param {(0 | 1)} is_favorited
 * @return {*}
 */
export function setTtsTimbreFavorite(material_id: number, is_favorited: 0 | 1) {
  return new Promise<void>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.ttsTimbreFavorite,
      method: 'POST',
      data: {
        material_id: Number(material_id), // 长id会被转为字符串，需要转为数字
        is_favorited,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve();
      return;
    }
    reject(response);
  });
}

/**
 * 视频压缩
 *
 * @export
 * @param {string} origin_url
 * @return {*}
 */
export function videoCompress(origin_url: string) {
  return new Promise<{ compress_url: string; }>(async (resolve, reject) => {
    try {
      const response = await agentTeamRequestApi?.request({
        url: APP.roboneo.api.videoCompress,
        method: 'POST',
        data: {
          origin_url,
        },
        timeout: 5 * 60 * 1000, // 超时时间5分钟
      });
      console.log('videoCompress response', response);
      if (response?.status === 200 && response?.data?.code === 0) {
        resolve({
          compress_url: response?.data?.result.result_url || origin_url,
        });
        return;
      }
      reject(response);
    } catch (error) {
      reject(error);
    }
  });
}
